import './styles.scss';
import React, {Dispatch, useCallback, useContext, useEffect, useRef, useState} from "react";

/* Types, Constants, Utils */
import * as Constants from "../../../constants";
import {IRecordsContext, RecordsContext} from "../../../types/document";

/* Bootstrap */
import Form from 'react-bootstrap/Form';
import {Button} from "react-bootstrap";

/* Redux */
import {updateSavedDocuments} from "../../../redux/slices/saved-slice";
import {ISavedState, TSavedDocument} from "../../../types/redux/saved";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../redux";
import {AnyAction} from "@reduxjs/toolkit";
import {IUserState} from "../../../types/redux/user";
import {addToast} from "../../../redux/slices/toast-slice";
import {IViewerState} from "../../../types/redux/viewer";
import {setLoginPromptModalActive} from "../../../redux/slices/login-modal-slice";
import {IResultsState} from "../../../types/redux/result";

/* Hooks */
import useSaveSelected from "../../../hooks/useSaveSelected";
import useError from "../../../hooks/useError";

/* API */
import SavedService from "../../../api/saved";

interface IProps {
  context: string
}

const Note = (props: IProps):JSX.Element => {
  const {
    context
  } = props;

  /* redux */
  const dispatch:Dispatch<AnyAction> = useDispatch();
  const savedState: ISavedState = useSelector<RootState, ISavedState>(state => state.saved);
  const userState: IUserState = useSelector<RootState, IUserState>(state => state.user);
  const resultsState: IResultsState = useSelector<RootState, IResultsState>(state => state.result);
  const viewerState: IViewerState = useSelector<RootState, IViewerState>(state => state.viewer);

  /* context */
  const records: any[] = useContext<IRecordsContext>(RecordsContext).records;
  const viewerRecord: IViewerState = useSelector<RootState, IViewerState>(state => state.viewer).viewerRecord;
  const [recordsToApply, setRecordsToApply] = useState<any[]>([]);

  /* state */
  /* @ts-ignore */
  const [content, setContent] = useState<string>('');
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [pendingNote, setPendingNote] = useState<boolean>(false);

  /* ref */
  const textAreaRef = useRef(null);

  /* hooks */
  const saveSelected: Function = useSaveSelected();
  const {handleError} = useError();

  useEffect(()=>{
    const _selectedRecords: any[] = resultsState.results.filter(r => (resultsState.selected_ids.findIndex(sr => sr.id === r.id) !== -1));
    const _records: any[] =  (records.length > 0) ? records : (_selectedRecords.length > 0) ? _selectedRecords : [viewerRecord];
    setRecordsToApply(_records)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, viewerRecord, resultsState.selected_ids])

  useEffect(()=> {
    /* update note for single document */
    if ((context === Constants.DOCUMENT)) {
      const savedDocumentData: TSavedDocument | undefined = savedState.documents.find((savedDocument: TSavedDocument) => savedDocument.documentId === recordsToApply[0]?.id)
      if (savedDocumentData && savedDocumentData.notes) {
        setContent(savedDocumentData.notes.replaceAll(' ;', ';'))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedState.documents, records, viewerRecord, viewerState.recordTool])

  const saveClearNote = useCallback(async (): Promise<any> =>{
    if (!userState.loggedIn) {
      setPendingNote(true)
      dispatch(setLoginPromptModalActive(true))
      return;
    }

    /* save any records not saved*/
    await saveSelected(recordsToApply);

    /* get full save record data */
    const savedDocumentData: TSavedDocument[] = [];
    recordsToApply.forEach((record:any) => {
      const savedDocument: TSavedDocument | undefined =  savedState.documents.find((savedDocument: TSavedDocument) => savedDocument.documentId === record.id);
      if (savedDocument) {
        savedDocumentData.push(savedDocument);
      }
    })

    if (savedDocumentData.length === 0) {
      // wasn't saved before creating a note, will get updated on useEffect above once redux registers the addition
      setPendingNote(true);
      return;
    }

    if (context === Constants.DOCUMENT) {
      const saveDocumentClone: TSavedDocument = {...savedDocumentData[0]};
      let message: string;
      if (isDirty) {
        /* updating a note */
        saveDocumentClone.notes = content;
        message = 'Note saved.';
      } else {
        /* clearing a note */
        saveDocumentClone.notes = undefined;
        message = 'Note cleared.';
      }

      /* from document card => replaces notes */
      SavedService.setDocumentNote(saveDocumentClone).then(response=>{
        if (!isDirty) {
          setContent('');
        }
        setIsDirty(false);

        dispatch(updateSavedDocuments(response));
        dispatch(addToast({
          bg: 'bg-success',
          msg: message,
          id: -1
        }))
      }).catch(e=>handleError(e));
    } else {
      /* bulk add note from toolbar ==> append note */
      const savedDocumentId: string[] = savedDocumentData.map(savedDocument => String(savedDocument.id));
      SavedService.appendDocumentNotes(savedDocumentId, content).then(response=>{
        dispatch(updateSavedDocuments(response))
        setContent('');
        setIsDirty(false);
        dispatch(addToast({
          bg: 'bg-success',
          msg: 'Note added to selected records.',
          id: -1
        }))
      }).catch(e=>handleError(e))
    }

  }, [content, isDirty, context, dispatch, saveSelected, savedState.documents,  userState.loggedIn, recordsToApply, handleError]);

  useEffect(()=>{
    if (userState.loggedIn && pendingNote){
      setPendingNote(false)
      saveClearNote();
    } else {
      setPendingNote(false);
      setIsDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState.loggedIn, pendingNote])

  useEffect(()=>{
    const numOfRows: number = content.split("\n").length;
    textAreaRef.current.setAttribute('rows', numOfRows);
    if (textAreaRef.current.scrollHeight > 0) {
      textAreaRef.current.setAttribute('rows', (textAreaRef.current.scrollHeight/20 - 1) );
    }
  }, [content]);

  return ( <>
    <span className={'notes'}>
      <Form.Control
        ref={textAreaRef}
          className={'note-input text-small d-inline-block align-top py-2'}
          as={'textarea'}
          type={'textarea'}
          placeholder={Constants.ADD_NOTE_PLACEHOLDER}
          rows={1}
          value={content}
          onChange={e=>{
            setIsDirty(true);
            setContent(e.target.value);
          }}
      />

      <div className={'text-end mt-2'}>
        {(content.length !== 0) && <Button
          className={'text-normal border-0 rounded-1 py-2 mb-1'}
          onClick={saveClearNote}
        >{(context === Constants.TOOLBAR) ? "Add to Notes" : isDirty ? "Save Notes" : "Clear Notes"}
        </Button>}
      </div>
    </span>
  </>)
}
export default Note;
